<template>
  <v-dialog v-model="display" persistent max-width="830" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title class="headline grey lighten-3" primary-title>
        <v-layout>
          <v-flex grow>
            {{title}}
          </v-flex>
          <v-spacer />
          <v-btn flat icon class="align--right" @click.stop="cancelHandler()">
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <v-form v-model="valid">
          <v-container grid-list-md>
            <v-layout row wrap>
              <v-flex xs5>
                <v-select
                  v-model="fee.fee_type_id"
                  :items="feeTypes"
                  label="Expense/Fee Type"
                  item-text="fee_name"
                  item-value="id"
                  :disabled="+fee.state === 1"
                  required
                  :rules="[(v) => !!v || 'Fee type is required']"
                  @change="updateFeeType($event)"
                ></v-select>
              </v-flex>
              <v-flex xs5>
                <v-menu
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  :close-on-content-click="false"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="fee.fee_date | moment('D/MM/YYYY')"
                      label="Date"
                      readonly
                      append-icon="event"
                      :disabled="+fee.state === 1"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fee.fee_date"
                    no-title
                  ></v-date-picker>
                </v-menu>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs2>
                <v-text-field
                  v-model="fee.fee"
                  label="Amount"
                  prefix="$"
                  type="number"
                  :disabled="+fee.state === 1"
                  required
                  :rules="[(v) => !!v || 'Fee amount is required']"
                  @input="updateFeeDetails(fee)"
                ></v-text-field>
              </v-flex>
              <v-flex xs5>
                <div class="grey--text text--darken-1">GST Options</div>
                <v-radio-group
                  v-model="fee.fee_gst_status"
                  row
                  class="mt-0"
                  :disabled="+fee.state === 1"
                  @click="updateFeeDetails(fee)"
                >
                  <v-radio label="None" :value="0" color="primary"></v-radio>
                  <v-radio
                    label="Include"
                    :value="1"
                    color="primary"
                  ></v-radio>
                  <v-radio
                    label="Exclude"
                    :value="2"
                    color="primary"
                  ></v-radio>
                </v-radio-group>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                  v-model="fee.fee_real"
                  label="Base Amount"
                  prefix="$"
                  type="number"
                  readonly
                  disabled
                ></v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                  v-model="fee.fee_gst"
                  label="GST"
                  prefix="$"
                  type="number"
                  disabled
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs12>
                <v-textarea
                  v-model="fee.fee_comment"
                  label="Comments"
                  rows="2"
                  :disabled="+fee.state === 1"
                ></v-textarea>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs8>
                <v-flex v-for="file in fee.files" :key="file.id" xs12>
                  <v-chip
                    :close="+fee.state !== 1"
                    outline
                    @input="
                      deleteFeeAttachment({
                        feeId: fee.id,
                        fileId: file.id,
                      })
                    "
                  >
                    <v-avatar>
                      <v-icon>attach_file</v-icon>
                    </v-avatar>
                    <a :href="getFileUrl(file)" target="new">{{ file.name }}</a>
                  </v-chip>
                </v-flex>
              </v-flex>
              <v-flex xs8 v-if="+fee.state !== 1">
                <v-checkbox v-model="activate" label="Activate this fee"></v-checkbox>
              </v-flex>
              <v-flex xs4 class="text-xs-right">
                <v-btn
                  v-if="+fee.state !== 1 && fee.id !== ''"
                  :loading="uploadingFile"
                  :disabled="uploadingFile"
                  color="primary"
                  class="white--text"
                  type="file"
                  @click.stop.prevent="initUpload()"
                >
                  <v-icon dark>attach_file</v-icon>Attach File
                </v-btn>
                <input
                  v-show="false"
                  ref="feeDocument"
                  type="file"
                  @change="uploadFile(fee.id, $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-container grid-list-md>
          <v-layout row wrap>
            <v-flex xs12>
              <v-btn
                v-if="+fee.state !== 1"
                class="primary pa-2 mb-2"
                block
                @click="saveFeeHandler()"
              >
                Save
              </v-btn>
            </v-flex>
            <v-flex xs12>
              <v-btn
                v-if="+fee.state !== 1"
                class="error pa-2 mb-2"
                block
                @click="deleteFeeHandler()"
              >
                Delete
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import alertHelper from '@/helpers/alert'
import { mapGetters, mapActions } from 'vuex'
import config from '@/config'

export default {
  name: 'FeeInfo',
  props: {
    selectedFee: {
      type: Number,
      default: function() {
        return 0
      },
    },
  },
  data() {
    return {
      display: true,
      valid: false,
      loader: null,
      uploadingFile: false,
      title: 'Fee or Expense Information'
    }
  },
  computed: {
    ...mapGetters({
      feeTypes: 'Configuration/getFeeTypes',
      getFeeById: 'HouseList/Fees/getFeeById',
      getFeeTypeById: 'Configuration/getFeeTypeById',
    }),
    fee() {
      return this.getFeeById(this.selectedFee)
    },
    activate: {
      get: function() { return this.fee.state !== 99} ,
      set: function(v) { this.fee.state = v? 0: 99 }
    }
  },
  methods: {
    ...mapActions({
      upload: 'HouseList/Fees/uploadFile',
      deleteFeeAttachment: 'HouseList/Fees/deleteAttachment',
      deleteFee: 'HouseList/Fees/delete',
      save: 'HouseList/Fees/update'
    }),
    initUpload(index) {
      this.$refs.feeDocument.click()
    },
    async uploadFile(feeId, event) {
      this.loader = 'loading'
      this.uploadingFile = true
      if (event.target.files.length > 0) {
        try {
          await this.upload({
            file: event.target.files[0],
            id: feeId,
            houseId: parseInt(this.$route.params.houseId),
          })
          this.uploadingFile = false
        } catch (error) {
          this.uploadingFile = false
        }
      }
    },
    close() {
      this.display = false
      this.$emit('update:selectedFee', '')
    },
    updateFeeDetails(fee) {
      if (fee.fee) {
        if (fee.fee_gst_status === 0) {
          fee.fee_real = fee.fee
          fee.fee_gst = 0
        } else if (fee.fee_gst_status === 1) {
          fee.fee_real = Number.parseFloat(fee.fee / 1.15).toFixed(2)
          fee.fee_gst = Number.parseFloat(fee.fee - fee.fee_real).toFixed(2)
        } else if (fee.fee_gst_status === 2) {
          fee.fee_real = fee.fee
          fee.fee_gst = Number.parseFloat(fee.fee * 0.15).toFixed(2)
        }
      }
    },
    getFileUrl(file) {
      return config.apiBaseUrl + file.url.substring(1)
    },
    async cancelHandler() {
      if (!this.fee.fee && !this.fee.fee_type_id) {
        this.deleteFee(this.fee)
      }
      this.close()
    },
    deleteFeeHandler() {
      this.deleteFee(this.fee)
      this.close()
    },
    updateFeeType(id) {
      this.fee.fee_type = this.getFeeTypeById(id)
    },
    saveFeeHandler() {
      if (!this.fee.fee_gst_status) {
        this.fee.fee_gst_status = 0
        this.updateFeeDetails(this.fee)
      }
      if (this.valid) {
        this.save({fee: this.fee, notify: true})
        this.close()
      } else {
        alertHelper.error('Please fill all necessary information')
      }
    }
  },
}
</script>

<style lang="stylus" scoped>
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
